import React, { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../../firebase";
import { useTheme } from "../../contexts/ThemeContext";
import {
  Upload,
  X,
  FileText,
  Camera,
  Loader,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

const InvoiceUpload = ({ user, onClose, onSuccess }) => {
  const { themeClasses } = useTheme();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [userPets, setUserPets] = useState([]);
  const [selectedPetId, setSelectedPetId] = useState("");
  const [loadingPets, setLoadingPets] = useState(true);
  const [extractionMethod, setExtractionMethod] = useState("original"); // "original" or "ai"

  // Initialize Firebase Functions
  const functions = getFunctions(app);

  // Load user's pets
  useEffect(() => {
    const loadUserPets = async () => {
      try {
        setLoadingPets(true);
        const getUserPets = httpsCallable(functions, "getUserPets");
        const result = await getUserPets();

        if (result.data.success) {
          setUserPets(result.data.pets);
          // Auto-select first pet if only one exists
          if (result.data.pets.length === 1) {
            setSelectedPetId(result.data.pets[0].id);
          }
        }
      } catch (error) {
        console.error("Failed to load pets:", error);
        setError("Failed to load your pets. Please try again.");
      } finally {
        setLoadingPets(false);
      }
    };

    if (user) {
      loadUserPets();
    }
  }, [user, functions]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;

    // Validate file type
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "application/pdf",
    ];
    if (!validTypes.includes(selectedFile.type)) {
      setError("Please select a valid image file (JPEG, PNG, WebP) or PDF");
      return;
    }

    // Validate file size (10MB limit)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError("File size must be less than 10MB");
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  const handleUpload = async () => {
    if (!file || !selectedPetId) {
      setError("Please select a pet and upload a file.");
      return;
    }

    try {
      setUploading(true);
      setProcessing(true);
      setError(null);

      // Convert file to base64
      const reader = new FileReader();
      const fileData = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Find selected pet name
      const selectedPet = userPets.find((pet) => pet.id === selectedPetId);
      const petName = selectedPet?.name || "Unknown Pet";

      let result;

      if (extractionMethod === "ai") {
        // Use the new Document AI only function
        const extractHealthRecordAI = httpsCallable(
          functions,
          "extractHealthRecordAI"
        );

        // Convert fileData to just base64 (remove data:mime;base64, prefix)
        const fileBase64 = fileData.split(",")[1];

        result = await extractHealthRecordAI({
          fileBase64,
          fileName: file.name,
          fileType: file.type,
          petId: selectedPetId,
          petName,
        });
      } else {
        // Use original extractHealthRecord function
        const extractHealthRecord = httpsCallable(
          functions,
          "extractHealthRecord"
        );
        result = await extractHealthRecord({
          fileData,
          fileName: file.name,
          fileType: file.type,
          petId: selectedPetId,
          petName,
        });
      }

      if (result.data.success) {
        setProcessing(false);
        setUploading(false);
        onSuccess();
      } else {
        throw new Error(
          result.data.message || "Failed to process health record"
        );
      }
    } catch (error) {
      console.error("Error uploading invoice:", error);
      setError(error.message || "Failed to process invoice. Please try again.");
      setUploading(false);
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`${themeClasses.bg.card} rounded-xl max-w-md w-full p-6 border ${themeClasses.border.primary}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2
            className={`text-xl font-bold ${themeClasses.text.primary} flex items-center`}
          >
            <Upload className={`w-5 h-5 mr-2 ${themeClasses.text.accent}`} />
            Upload Invoice
          </h2>
          <button
            onClick={onClose}
            className={`p-2 ${themeClasses.text.secondary} hover:${themeClasses.text.primary} hover:${themeClasses.bg.secondary} rounded-lg transition-colors`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Extraction Method Selection */}
        <div className="mb-6">
          <label
            className={`block text-sm font-medium ${themeClasses.text.primary} mb-2`}
          >
            Extraction Method
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="extractionMethod"
                value="original"
                checked={extractionMethod === "original"}
                onChange={(e) => setExtractionMethod(e.target.value)}
                className="mr-2"
              />
              <span className={themeClasses.text.primary}>
                Original (Hybrid)
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="extractionMethod"
                value="ai"
                checked={extractionMethod === "ai"}
                onChange={(e) => setExtractionMethod(e.target.value)}
                className="mr-2"
              />
              <span className={themeClasses.text.primary}>
                Document AI Only
              </span>
            </label>
          </div>
          <p className={`${themeClasses.text.secondary} text-xs mt-1`}>
            {extractionMethod === "ai"
              ? "Clean extraction using only Google Document AI"
              : "Original method with text parsing and Document AI"}
          </p>
        </div>

        {/* Pet Selection */}
        <div className="mb-6">
          <label
            className={`block text-sm font-medium ${themeClasses.text.primary} mb-2`}
          >
            Select Pet
          </label>
          {loadingPets ? (
            <div
              className={`${themeClasses.bg.secondary} rounded-lg p-3 flex items-center space-x-2`}
            >
              <Loader
                className={`w-4 h-4 ${themeClasses.text.accent} animate-spin`}
              />
              <span className={themeClasses.text.secondary}>
                Loading your pets...
              </span>
            </div>
          ) : userPets.length === 0 ? (
            <div
              className={`${themeClasses.bg.tertiary} border ${themeClasses.border.primary} rounded-lg p-3`}
            >
              <p className={`${themeClasses.text.secondary} text-sm`}>
                No pets found. Please register a pet first before uploading
                health records.
              </p>
            </div>
          ) : (
            <select
              value={selectedPetId}
              onChange={(e) => setSelectedPetId(e.target.value)}
              className={`w-full ${themeClasses.bg.input} border ${themeClasses.border.primary} ${themeClasses.text.primary} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
              <option value="">Choose a pet...</option>
              {userPets.map((pet) => (
                <option key={pet.id} value={pet.id}>
                  {pet.name} ({pet.species} - {pet.breed})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Upload Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? `${themeClasses.border.accent} ${themeClasses.bg.tertiary}`
              : `${themeClasses.border.secondary} hover:${themeClasses.border.primary}`
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {file ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center">
                <FileText className={`w-12 h-12 ${themeClasses.text.accent}`} />
              </div>
              <div>
                <p className={`${themeClasses.text.primary} font-medium`}>
                  {file.name}
                </p>
                <p className={`${themeClasses.text.secondary} text-sm`}>
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button
                onClick={() => setFile(null)}
                className={`${themeClasses.text.error} hover:${themeClasses.text.error} text-sm`}
              >
                Remove file
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-center">
                <Camera
                  className={`w-12 h-12 ${themeClasses.text.secondary}`}
                />
              </div>
              <div>
                <p className={`${themeClasses.text.primary} font-medium mb-2`}>
                  Drop your invoice here or click to browse
                </p>
                <p className={`${themeClasses.text.secondary} text-sm`}>
                  Supports JPEG, PNG, WebP, and PDF files up to 10MB
                </p>
              </div>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className={`inline-block px-4 py-2 ${themeClasses.bg.button} text-white rounded-lg cursor-pointer transition-colors`}
              >
                Choose File
              </label>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div
            className={`mt-4 p-3 ${themeClasses.bg.tertiary} border ${themeClasses.border.primary} rounded-lg flex items-center space-x-2`}
          >
            <AlertCircle
              className={`w-5 h-5 ${themeClasses.text.error} flex-shrink-0`}
            />
            <p className={`${themeClasses.text.error} text-sm`}>{error}</p>
          </div>
        )}

        {/* Processing Status */}
        {(uploading || processing) && (
          <div
            className={`mt-4 p-3 ${themeClasses.bg.tertiary} border ${themeClasses.border.primary} rounded-lg flex items-center space-x-2`}
          >
            <Loader
              className={`w-5 h-5 ${themeClasses.text.accent} animate-spin flex-shrink-0`}
            />
            <div>
              <p className={`${themeClasses.text.accent} text-sm font-medium`}>
                {uploading ? "Uploading file..." : "Processing invoice..."}
              </p>
              <p className={`${themeClasses.text.secondary} text-xs`}>
                {extractionMethod === "ai"
                  ? "Using clean Document AI extraction"
                  : "Using hybrid extraction method"}
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className={`px-4 py-2 ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors`}
            disabled={uploading || processing}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || !selectedPetId || uploading || processing}
            className={`px-6 py-2 ${themeClasses.bg.button} text-white rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {uploading || processing ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            <span>
              {uploading
                ? "Uploading..."
                : processing
                ? "Processing..."
                : `Upload & Process ${
                    extractionMethod === "ai" ? "(AI)" : "(Hybrid)"
                  }`}
            </span>
          </button>
        </div>

        {/* Help Text */}
        <div className={`mt-4 p-3 ${themeClasses.bg.secondary} rounded-lg`}>
          <p className={`${themeClasses.text.secondary} text-xs`}>
            💡 <strong>Tip:</strong> For best results, ensure your invoice is
            clearly visible with good lighting. Our AI will automatically
            extract clinic information, services, medications, and costs.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvoiceUpload;
